import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import "../styles/ModerationDashboard.css";

// 共通UI
import ElegantSidebar from "../../../shared/components/layout/ElegantSidebar";
import InfoPopup from "../../../shared/components/ui/overlays/InfoPopup";
import ConfirmModal from "../../../shared/components/ui/overlays/ConfirmModal";
import ErrorBanner from "../../../shared/components/ui/feedback/ErrorBanner";

// 管理画面UI
import ImageModerationGrid from "./ImageModerationGrid";
import ModerationStats from "./ModerationStats";
import ModerationPagination from "../../../shared/components/hoc/ModerationPagination";

// hooks
import { useSidebarControl } from "../hooks/useSidebarControl";
import { useToastNotification } from "../../../shared/hooks/ui/useToastNotification";
import { useImageUpdate } from "../../../contexts/ImageUpdateContext";
import { useImageFetching } from "../../../shared/hooks/data/useImageFetching";
import { useImageFetchingLight } from "../../../shared/hooks/data/useImageFetchingLight";

// utils/types
import { validateRoomId } from "../../../shared/utils/validation/roomValidation";
import { getApiConfig } from "../../../shared/config/apiConfig";
import { getBaseUrl } from "../../../config/urls";
import { ImageData } from "../../../shared/types/gallery";
import { Menu, X } from "lucide-react";

// --- 型定義 ---
type ModerationFilter = "unmoderated" | "moderated-ok" | "moderated-ng" | "all";
interface ErrorState {
  type: 'none' | 'roomId' | 'network' | 'server' | 'partial' | 'unknown';
  message: string;
  details?: string;
  retryable: boolean;
}

// --- サブコンポーネント ---
const LoadingScreen: React.FC<{ sidebarOpen: boolean, isDesktop: boolean, handleSidebarOverlayClick: () => void, toggleSidebar: () => void }> = ({
  sidebarOpen, isDesktop, handleSidebarOverlayClick, toggleSidebar
}) => (
  <div className="wedding-gallery-layout">
    {!isDesktop && sidebarOpen && (
      <div className="sidebar-overlay" onClick={handleSidebarOverlayClick}></div>
    )}
    <div className={`sidebar-container ${isDesktop ? 'desktop' : 'mobile'} ${sidebarOpen ? 'open' : ''}`}>
      <ElegantSidebar />
    </div>
    <div className={`wedding-gallery-content ${isDesktop ? 'desktop' : 'mobile'}`}>
      {!isDesktop && (
        <button className="sidebar-toggle-button" onClick={toggleSidebar}>
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      )}
      <div className="loading-container">
        <div className="elegant-loading-spinner"></div>
        <p className="loading-text">画像を読み込み中...</p>
      </div>
    </div>
  </div>
);

const LoadingModal: React.FC = () => (
  <div className="elegant-loading-overlay">
    <div className="elegant-loading-content">
      <div className="elegant-loading-spinner large"></div>
      <h3 className="loading-title">処理中</h3>
      <p className="loading-description">スクリーンへ配信しています...</p>
    </div>
  </div>
);

const ToastNotification: React.FC<{ message: string, type: string }> = ({ message, type }) => (
  <div className={`toast-notification ${type}`}>
    <div className="toast-content">
      <span className="toast-message">{message}</span>
    </div>
  </div>
);

// --- メインコンポーネント ---
const ModerationDashboard: React.FC = () => {
  // --- State ---
  const [images, setImages] = useState<ImageData[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<ModerationFilter>("unmoderated");
  const [searchParams] = useSearchParams();

  // ★ ページ方式（行ベース）
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(6); // 1ページの行数
  const [totalRows, setTotalRows] = useState(0);     // 子から通知
  const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));

  const [optimalImageSize, setOptimalImageSize] = useState(150); // Justified の targetRowHeight
  const [hasNextPage, setHasNextPage] = useState(false);         // サーバページングがある場合のみ
  const [loadingMore, setLoadingMore] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [confirmModalVisibleNg, setConfirmModalVisibleNg] = useState(false);
  const [loadingModalVisible, setLoadingModalVisible] = useState(false);

  const [errorState, setErrorState] = useState<ErrorState>({ type: 'none', message: '', retryable: false });

  // --- hooks ---
  const urlBase = getBaseUrl();
  const { notifyImageUpdate, onImageUpdate } = useImageUpdate();
  const apiConfig = getApiConfig();
  const useLightApi = apiConfig.useLightApi;

  const { sidebarOpen, isDesktop, toggleSidebar, handleSidebarOverlayClick } = useSidebarControl();
  const { toastMessage, toastType, showToast } = useToastNotification();

  const { fetchImagesWithControl, loading: fetchLoading, cancelFetching, isRequestActive } = useImageFetching({
    urlBase,
    onError: (error) => setErrorState(error)
  });
  const { fetchImagesLight, loading: fetchLightLoading, cancelFetching: cancelLightFetching, isRequestActive: isLightRequestActive } = useImageFetchingLight({
    urlBase,
    onError: (error) => setErrorState(error)
  });

  // --- refs ---
  const fetchImagesWithControlRef = useRef(fetchImagesWithControl);
  fetchImagesWithControlRef.current = fetchImagesWithControl;

  // --- Effect: URLからRoomIdを取得 ---
  useEffect(() => {
    const roomIdFromUrl = searchParams.get("roomid");
    if (roomIdFromUrl) {
      console.log("[ModerationDashboard] Found roomId in URL:", roomIdFromUrl);
      const { setRoomId } = require("../../../shared/utils/validation/roomValidation");
      setRoomId(roomIdFromUrl);
    }
  }, [searchParams]);

  // --- Effect: ガイド表示 ---
  useEffect(() => {
    const token = localStorage.getItem("token");
    const hasSeenGuide = localStorage.getItem(`guideShown_${token}`);
    if (token && !hasSeenGuide) {
      setShowPopup(true);
      localStorage.setItem(`guideShown_${token}`, "true");
    }
  }, []);

  // --- Effect: 初期画像取得 ---
  useEffect(() => {
    const timer = setTimeout(fetchImages, 200);
    return () => {
      clearTimeout(timer);
      useLightApi ? cancelLightFetching() : cancelFetching();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Effect: 画像更新通知 ---
  useEffect(() => {
    const unsubscribe = onImageUpdate(fetchImages);
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onImageUpdate]);

  // --- Effect: localStorage画像アップロード検知 ---
  useEffect(() => {
    const checkForUploadedImages = () => {
      if (localStorage.getItem('imageUploaded') === 'true') {
        fetchImages();
        localStorage.removeItem('imageUploaded');
        localStorage.removeItem('imageUploadTimestamp');
      }
    };
    checkForUploadedImages();
    window.addEventListener('focus', checkForUploadedImages);
    return () => window.removeEventListener('focus', checkForUploadedImages);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Effect: 画面サイズで Justified の目標高さ＆行数を調整 ---
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;

      // UI要素の固定高さを見積もり
      const headerHeight = 80;      // ヘッダー
      const statsHeight = 120;      // 統計カード
      const actionsHeight = 60;     // アクションボタン
      const paginationHeight = 50;  // ページネーション
      const margins = 40;           // 余白
      const uiTotalHeight = headerHeight + statsHeight + actionsHeight + paginationHeight + margins;

      // 実際の画像表示エリアの高さ
      const availableHeight = Math.max(200, height - uiTotalHeight);

      // 画面幅から目標行高を推定（以前の列計算は不要）
      // 小画面はやや小さめ、大画面は大きめ
      const baseTarget = width < 480 ? 120 : width < 768 ? 150 : width < 1024 ? 180 : 200;
      const targetRowHeight = Math.max(100, Math.min(240, baseTarget));

      // 1行あたりの実効高さ（画像＋行間）
      const spacing = 12;
      const rowBlock = targetRowHeight + spacing;

      // 表示可能行数（1〜8にクリップ）
      const maxRows = Math.floor(availableHeight / rowBlock);
      const optimalRows = Math.min(8, Math.max(2, maxRows));

      setOptimalImageSize(targetRowHeight);
      setRowsPerPage(optimalRows);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [images, filter]);

  useEffect(() => {
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;
    const prevBodyHeight = document.body.style.height;

    return () => {
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;
      document.body.style.height = prevBodyHeight;
    };
  }, []);

  // --- 画像取得 ---
  const fetchImages = useCallback(async () => {
    setLoading(true);
    console.log("[ModerationDashboard] Current roomid in sessionStorage:", sessionStorage.getItem("roomid"));
    const roomValidation = validateRoomId();
    if (!roomValidation.isValid || !roomValidation.roomId || !urlBase.trim()) {
      setImages([]);
      setHasNextPage(false);
      setLoading(false);
      return;
    }
    try {
      if (useLightApi) {
        const result = await fetchImagesLight({
          page: 0,
          size: 1000,  // モデレーション管理では全画像を表示するため大きなサイズを指定
          moderationFilter: 'all',  // 常に全画像を取得
          sort: 'recent'
        });
        if (!result?.success || !Array.isArray(result.images)) {
          setImages([]);
          setHasNextPage(false);
          setLoading(false);
          return;
        }
        const fetchedImages: ImageData[] = result.images.map(img => ({
          id: img.pictureId,
          base64: '',
          imageUrl: `${urlBase}${img.imageUrl}`,
          isModerated: img.isModerated,
          selected: false,
        }));
        setImages(prev =>
          fetchedImages.map(newImg => ({
            ...newImg,
            selected: prev.find(existing => existing.id === newImg.id)?.selected ?? false,
          }))
        );
        setHasNextPage(result.hasNext);
      } else {
        const result = await fetchImagesWithControlRef.current();
        if (!result?.success || !Array.isArray(result.images)) {
          setImages([]);
          setHasNextPage(false);
          setLoading(false);
          return;
        }
        const fetchedImages: ImageData[] = result.images.map((img: any) => ({
          id: img.pictureid,
          base64: img.base64,
          imageUrl: `data:image/png;base64,${img.base64}`,
          isModerated: img.isModerated,
          selected: false,
        }));
        setImages(prev =>
          fetchedImages.map(newImg => ({
            ...newImg,
            selected: prev.find(existing => existing.id === newImg.id)?.selected ?? false,
          }))
        );
        setHasNextPage(false);
      }
    } catch (error) {
      if (!axios.isCancel(error)) {
        setImages([]);
        setHasNextPage(false);
      }
    } finally {
      setLoading(false);
    }
  }, [urlBase, useLightApi, filter, fetchImagesLight, apiConfig.defaultPageSize]);

  // --- エラークリア ---
  const clearError = useCallback(() => setErrorState({ type: 'none', message: '', retryable: false }), []);

  // --- 画像選択 ---
  const toggleSelectImage = useCallback((id: number) => {
    setImages(prev => prev.map(img => img.id === id ? { ...img, selected: !img.selected } : img));
  }, []);

  // --- 承認/拒否処理 ---
  const handleModeration = async (endpoint: string, status: number, successMsg: string, errorMsg: string, closeModal: () => void) => {
    setLoadingModalVisible(true);
    closeModal();
    const selectedIds = images.filter(img => img.selected).map(img => img.id);
    const token = localStorage.getItem("token");
    try {
      await axios.post(
        `${urlBase}${endpoint}`,
        selectedIds,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      setImages(prev => prev.map(img => img.selected ? { ...img, isModerated: status, selected: false } : img));
      notifyImageUpdate();
      showToast(`${selectedIds.length} ${successMsg}`, "success");
    } catch {
      showToast(errorMsg, "error");
    } finally {
      setLoadingModalVisible(false);
    }
  };

  // --- フィルタ ---
  const filteredImages = images.filter(img =>
    filter === "moderated-ok" ? img.isModerated === 1 :
      filter === "moderated-ng" ? img.isModerated === 2 :
        filter === "unmoderated" ? img.isModerated === 0 : true
  );

  // ★ Justified ではレイアウト側が行を組むので slice はしない
  const currentImages = filteredImages;

  console.log('ModerationDashboard - Row-based pagination:', {
    currentPage,
    rowsPerPage,
    totalRows,
    totalPages,
    totalImages: images.length,
    filteredImages: filteredImages.length,
    targetRowHeight: optimalImageSize,
    filter
  });

  // --- 統計 ---
  const selectedCount = images.filter(img => img.selected).length;
  const unmoderatedCount = images.filter(img => img.isModerated === 0).length;
  const approvedCount = images.filter(img => img.isModerated === 1).length;
  const rejectedCount = images.filter(img => img.isModerated === 2).length;

  const BulkActionsBar: React.FC<{
    count: number;
    onApprove: () => void;
    onReject: () => void;
    disabled?: boolean;
  }> = ({ count, onApprove, onReject, disabled }) => {
    return (
      <div className="bulk-actions-bar">
        <div className="bulk-actions-left">
          <span className="bulk-actions-count">
            選択中: <b>{count}</b>
          </span>
        </div>
        <div className="bulk-actions-right">
          <button className="btn-reject" onClick={onReject} disabled={disabled}>
            拒否
          </button>
          <button className="btn-approve" onClick={onApprove} disabled={disabled}>
            承認
          </button>
        </div>
      </div>
    );
  };

  // --- ローディング画面 ---
  if (loading) {
    return (
      <LoadingScreen
        sidebarOpen={sidebarOpen}
        isDesktop={isDesktop}
        handleSidebarOverlayClick={handleSidebarOverlayClick}
        toggleSidebar={toggleSidebar}
      />
    );
  }

  // --- メイン画面 ---
  return (
    <div className="wedding-gallery-layout">
      {/* サイドバーオーバーレイ */}
      {!isDesktop && sidebarOpen && (
        <div className="sidebar-overlay" onClick={handleSidebarOverlayClick}></div>
      )}
      {/* サイドバー */}
      <div className={`sidebar-container ${isDesktop ? 'desktop' : 'mobile'} ${sidebarOpen ? 'open' : ''}`}>
        <ElegantSidebar />
      </div>
      <div className={`wedding-gallery-content ${isDesktop ? 'desktop' : 'mobile'}`}>
        {/* トグルボタン */}
        {!isDesktop && (
          <button className="sidebar-toggle-button" onClick={toggleSidebar}>
            {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        )}
        {/* ポップアップ */}
        {showPopup && <InfoPopup onClose={() => setShowPopup(false)} />}
        {/* エラーバナー */}
        <ErrorBanner
          error={errorState}
          onRetry={fetchImages}
          onDismiss={clearError}
          loading={(useLightApi ? fetchLightLoading : fetchLoading) || loading}
        />
        {/* 確認モーダル */}
        {confirmModalVisible && (
          <ConfirmModal
            message="選択した画像を承認してスクリーンへ送信しますか？"
            onConfirm={() => handleModeration("/api/moderation/update-ok", 1, "件の画像を承認しました", "画像の承認に失敗しました", () => setConfirmModalVisible(false))}
            onCancel={() => setConfirmModalVisible(false)}
          />
        )}
        {confirmModalVisibleNg && (
          <ConfirmModal
            message="選択した画像を拒否しますか？"
            onConfirm={() => handleModeration("/api/moderation/update-ng", 2, "件の画像を拒否しました", "画像の拒否に失敗しました", () => setConfirmModalVisibleNg(false))}
            onCancel={() => setConfirmModalVisibleNg(false)}
          />
        )}
        {/* ローディングモーダル */}
        {loadingModalVisible && <LoadingModal />}
        {/* ヘッダー */}
        <div className="scale-root">
          <header className="gallery-header">
            <h1 className="gallery-title">画像検閲管理</h1>
          </header>
          {/* 統計情報 */}
          <ModerationStats
            unmoderatedCount={unmoderatedCount}
            approvedCount={approvedCount}
            rejectedCount={rejectedCount}
            totalCount={images.length}
            onFilterChange={(v) => { setFilter(v); setCurrentPage(1); }}
            onPageReset={() => setCurrentPage(1)}
          />

          <BulkActionsBar
            count={selectedCount}
            onApprove={() => setConfirmModalVisible(true)}
            onReject={() => setConfirmModalVisibleNg(true)}
            disabled={
              loadingModalVisible ||
              loading ||
              (useLightApi ? fetchLightLoading : fetchLoading)
            }
          />

          {/* 画像グリッド（行ベースページング） */}
          <ImageModerationGrid
            layout="rows"
            targetRowHeight={optimalImageSize}
            spacing={12}
            images={currentImages}
            onToggle={toggleSelectImage}
            hasNextPage={hasNextPage}
            isLoadingMore={loadingMore}
            onLoadMore={async () => {
              // ページ方式でもサーバページングを併用するならここでロード
              const currentLoading = useLightApi ? fetchLightLoading : fetchLoading;
              const currentIsActive = useLightApi ? isLightRequestActive : isRequestActive;
              if (!loadingMore && !currentLoading && !currentIsActive) {
                setLoadingMore(true);
                try { await fetchImages(); } finally { setLoadingMore(false); }
              }
            }}
            loading={loading}
            isRequestActive={useLightApi ? isLightRequestActive : isRequestActive}
            paginateByRows={true}
            page={currentPage}
            rowsPerPage={rowsPerPage}
            onRowsChange={(rows) => {
              setTotalRows(rows);
              const pages = Math.max(1, Math.ceil(rows / rowsPerPage));
              if (currentPage > pages) setCurrentPage(pages);
            }}
          />

          {/* ページネーション（行ベース） */}
          <ModerationPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            isNextDisabled={currentPage >= totalPages}
            isPrevDisabled={currentPage === 1}
          />
        </div>
        {/* トースト通知 */}
        {toastMessage && <ToastNotification message={toastMessage} type={toastType} />}
      </div>
    </div>
  );
};

export default ModerationDashboard;
